import { motion } from 'framer-motion';

import { HighlightedSelections } from '@/components/HighlightedSelections';
import { HighlightedSlots } from '@/components/HighlightedSlots';

import type { MeaningCard } from './page';

/**
 * Card for the redesigned Meaning feed (design bundle screens 13a/13b), shared by the desktop and
 * mobile lists. `layoutId` is only passed on mobile, where it drives the shared-layout grow
 * animation into the opened card (see ExpandedMeaningCard's matching layoutId) — omitted on
 * desktop, where MeaningDesktopDetailShell's aside already carries that transition.
 */
export function MeaningFeedCard({
  card,
  onOpen,
  layoutId,
}: {
  card: MeaningCard;
  onOpen: () => void;
  layoutId?: string;
}) {
  return (
    <motion.article
      layoutId={layoutId}
      initial={layoutId ? { opacity: 0 } : false}
      animate={{ opacity: 1 }}
      exit={layoutId ? { opacity: 0, y: -8 } : undefined}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="mb-3.5 flex flex-col gap-2.5 rounded-[14px] border border-[#ece9e2] bg-white px-[18px] pb-3.5 pt-4 font-[family-name:var(--font-meaning-sans)] outline-none transition hover:-translate-y-0.5 hover:shadow-[0_3px_12px_rgba(15,23,42,0.08)] focus-visible:ring-2 focus-visible:ring-[#2f5c4e]/30"
    >
      <div className="min-w-0 font-display text-[23px] font-semibold leading-[1.2] text-[#1e1c18]">
        <HighlightedSlots text={card.phrase} active={card.isSlotted} className="font-normal text-[#a39c8d]" />
      </div>

      {card.context && (
        <div className="border-l-[3px] border-[#f0c066] pl-[11px] font-display text-[16px] italic leading-[1.5] text-[#3d3a33]">
          <HighlightedSelections text={card.context} selections={card.selections} />
        </div>
      )}

      <div className="border-t border-[#f4f1ec] pt-2.5">
        {card.translation && (
          <div className="text-[14.5px] leading-[1.45] text-[#7b7669]">{card.translation}</div>
        )}
        {card.definition && <div className="mt-[3px] text-[15px] leading-[1.5] text-[#2b2821]">{card.definition}</div>}
      </div>

      {card.tags.length > 0 && (
        <div className="mt-auto flex flex-wrap items-center gap-1.5 border-t border-[#f4f1ec] pt-[11px]">
          {card.tags.map((tag) => (
            <span
              key={tag.id}
              className="whitespace-nowrap rounded-full bg-[#f3f1ec] px-[10px] py-[4px] text-[13px] font-semibold text-[#57534a]"
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </motion.article>
  );
}
